import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  RadioGroup,
  Radio,
  Stack,
  Select,
  InputGroup,
  InputRightElement,
  IconButton,
  Image,
  Spinner,
  Checkbox,
  Tooltip,
} from "@chakra-ui/react";
import { ModalDelete } from "@root/components/modal-delete";

import {
  AuthFormContainer,
  AuthCardEllipse,
  AuthCardTopFrame,
  AuthHeading,
  AuthSubheading,
  AuthInputBox,
  AuthInputField,
  AuthLabelText,
  AuthButton,
  AuthButtonWrapper,
  AuthCheckboxText,
  LinkText,
} from "@root/commonStyles/commonStyle";
import { TitledLogo } from "@root/assets/images";
import { ROUTE_NAMES, USER_TYPES } from "@root/constants";
import { Field, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { NounCube } from "@root/assets/images";
import { useDispatch } from "react-redux";
import { signup } from "@root/store/thunks/auth";
import { OrganizationService } from "@root/services/organization";
import { OpenEye, CloseEye } from "@root/assets/admin";
import { USER_TYPE } from "@root/constants/common";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import { useJournalistContext } from "@root/context/journalist-context";
import CustomSelect from "@root/components/common/CustomSelect";

export const Signup: React.FC = () => {
  const { t } = useTranslation("common");
  const {
    logoUrl,
    defaultLogoUrl,
    slug,
    loading: brandingLoading,
  } = useOrganizationBranding();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userType, setUserType] = useState(USER_TYPE.SUBSCRIBER);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchDepartments, fetchMoreDepartments, departmentsData } = useJournalistContext();
  const [organizationOptions, setOrganizationOptions] = useState<
    { id: string; companyName: string }[]
  >([]);

  const fetchOrganizations = async (searchKeyword: string = "") => {
    try {
      const orgService = new OrganizationService();
      const response = await orgService?.fetchPublicCompanyNames({
        role:
          userType === USER_TYPE.SUBSCRIBER
            ? USER_TYPES.SUBSCRIBER
            : USER_TYPES.NON_SUBSCRIBER,
        ...(slug && { slug }),
        search: searchKeyword,
      });

      const orgs = (response?.data?.data || []).filter((org: any) => org?.companyName);
      setOrganizationOptions(orgs);
    } catch (e) {
      setOrganizationOptions([]);
    }
  };

  React.useEffect(() => {
    if (brandingLoading) return;
    fetchOrganizations();
  }, [userType, slug, brandingLoading]);
  console.log("departmentsData", departmentsData);
  const initialValues = {
    fullName: "",
    email: "",
    loginId: "",
    phone: "",
    organization: "",
    departement: "",
    password: "",
    confirmPassword: "",
    privacyPolicy: false,
  };

  // Holds the submitted form while we ask whether to replace a sign-up that is
  // already verified and waiting on the subscriber. Non-null = dialog open.
  const [pendingApprovalValues, setPendingApprovalValues] = useState<
    typeof initialValues | null
  >(null);

  // Password validation helpers
  const passwordValid = (password: string) => password?.length >= 8;
  const passwordHasNumber = (password: string) => /\d/.test(password);
  const passwordsMatch = (password: string, confirm: string) =>
    password === confirm && password !== "";

  const handleSubmit = async (
    values: typeof initialValues,
    replaceExisting = false
  ) => {
    setLoading(true);

    let orgPayload: Record<string, any> = {};

    if (values?.organization) {
      orgPayload.subscriberId = values?.organization;
      orgPayload.companyName = organizationOptions?.find(
        (org) => org?.id === values?.organization
      )?.companyName;
    }
    dispatch(
      signup({
        payload: {
          name: values?.fullName?.trim(),
          email: values?.email?.trim(),
          ...(values?.loginId?.trim() && { loginId: values.loginId.trim() }),
          phone: values?.phone?.trim(),
          password: values?.password?.trim(),
          ...orgPayload,
          department: departmentsData?.rows?.find(
            (dept: any) => dept?.id === values?.departement
          )?.title,
          slug: slug ?? "",
          organizationType: userType === USER_TYPE.SUBSCRIBER ? 1 : 2,
          // Only sent on the retry after the applicant confirmed they want to
          // replace a request that is already awaiting subscriber approval.
          ...(replaceExisting && { replaceExisting: true }),
        },
        onSuccess: (response: any) => {
          setLoading(false);
          const emailToVerify = values?.email?.trim();
          const companyName = organizationOptions?.find(
            (org) => org?.id === values?.organization
          )?.companyName ?? values?.organization ?? "";

          if (response?.statusCode === 201 || response?.statusCode === 200) {
            sessionStorage?.setItem("signup_email", emailToVerify);
            sessionStorage?.setItem("signup_company", companyName);
            sessionStorage?.setItem("signup_usertype", userType);

            navigate(ROUTE_NAMES.SIGNUP_EMAIL_VERIFICATION, {
              state: { email: emailToVerify, companyName, usertype: userType },
            });
          }
        },
        onError: (error: any) => {
          setLoading(false);
          // 409 = an already-verified request is queued for approval. Ask
          // before replacing it instead of failing the sign-up outright.
          if (error?.statusCode === 409) {
            setPendingApprovalValues(values);
          }
        },
      })
    );
  };

  return (
    <AuthFormContainer minW={["100%", "800px"]}>
      <AuthCardEllipse />
      <AuthCardTopFrame mb={"32px"}>
        <Flex align="center" minH="38px">
          {brandingLoading ? (
            <Spinner size="md" />
          ) : (
            <Image
              src={logoUrl || defaultLogoUrl}
              alt="logo"
              maxH="38px"
              objectFit="contain"
            />
          )}
        </Flex>
        <Flex flexDirection={"column"} alignItems="center" textAlign="center">
          <AuthHeading>{t("signup.subTitle")}</AuthHeading>
          <Text
            textColor="#798692"
            dangerouslySetInnerHTML={{ __html: t("signup.subtitle_1") }}
          />
        </Flex>
      </AuthCardTopFrame>
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: any = {};

          if (!values?.fullName?.trim()) {
            errors.fullName = t("signup.fullNameRequired");
          }

          if (!values?.email) {
            errors.email = t("signup.emailRequired");
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values?.email)
          ) {
            errors.email = t("signup.invalidEmail");
          }

          if (values?.phone) {
            const phoneDigits = values?.phone?.replace(/\s/g, "")?.length || 0;
            if (phoneDigits < 8 || phoneDigits > 12) {
              errors.phone = t("signup.phoneDigitsError");
            }
          }

          if (!values?.organization) {
            errors.organization = t("signup.organizationRequired");
          }

          if (!values?.password) {
            errors.password = t("signup.passwordRequired");
          } else if (values?.password?.length < 8) {
            errors.password = t("signup.passwordMinLength");
          } else if (!/\d/.test(values?.password)) {
            errors.password = t("signup.passwordMinNumber");
          }

          if (values?.password && values?.confirmPassword && values?.password !== values?.confirmPassword) {
            errors.confirmPassword = t("signup.passwordMatch");
          } else if (values?.password && !values?.confirmPassword) {
            errors.confirmPassword = t("signup.passwordMatch");
          }

          // if (!values.confirmPassword) {
          //   errors.confirmPassword = "Confirm password is required";
          // } else if (values.confirmPassword !== values.password) {
          //   errors.confirmPassword = "Passwords do not match";
          // }

          if (!values?.privacyPolicy) {
            errors.privacyPolicy = t("signup.privacyPolicyRequired", "You must agree to the privacy policy");
          }

          return errors;
        }}
        onSubmit={(values) => handleSubmit(values)}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          errors,
          touched,
          handleBlur,
          isValid,
          dirty,
        }) => {
          const allFieldsFilled = isValid && dirty && !loading;

          const getFirstMissingFieldMessage = (): string => {
            if (!values?.fullName?.trim()) return t("signup.tooltipFullName");
            if (!values?.email?.trim()) return t("signup.tooltipEmail");
            if (!values?.organization) return t("signup.tooltipOrganization");
            if (!values?.password) return t("signup.tooltipPassword");
            if (!values?.confirmPassword || values?.password !== values?.confirmPassword) return t("signup.tooltipConfirmPassword");
            if (!values?.privacyPolicy) return t("signup.tooltipPrivacyPolicy");
            return "";
          };

          const tooltipMessage = !allFieldsFilled ? getFirstMissingFieldMessage() : "";

          return (
            <form style={{ width: "100%" }} onSubmit={handleSubmit}>
              <Flex gap={"20px"} flexDir={["column", "row", "row"]}>
                <Flex flexDir={"column"} gap={"22px"} width={["100%", "100%", "50%"]}>
                  {" "}
                  <GridItem colSpan={1}>
                    <AuthLabelText>{t("signup.fullName")} <Text as="span" color="red.500">*</Text></AuthLabelText>
                    <AuthInputBox>
                      <AuthInputField
                        id="fullName"
                        name="fullName"
                        type="text"
                        placeholder={t(
                          "signup.fullNamePlaceholder",
                          "Enter your full name"
                        )}
                        value={values?.fullName}
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setFieldValue("fullName", e?.target?.value)
                        }
                      />
                      {touched?.fullName && errors?.fullName && (
                        <Text fontSize="sm" color="red.500" mt={1}>
                          {errors?.fullName}
                        </Text>
                      )}
                    </AuthInputBox>
                  </GridItem>
                  <GridItem colSpan={1} mb="2" colStart={1} rowStart={2}>
                    <AuthLabelText>{t("signup.email")} <Text as="span" color="red.500">*</Text></AuthLabelText>
                    <AuthInputBox>
                      <AuthInputField
                        id="email"
                        name="email"
                        type="email"
                        placeholder={t(
                          "signup.emailPlaceholder",
                          "Enter your email"
                        )}
                        value={values?.email}
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setFieldValue("email", e?.target?.value)
                        }
                      />
                      {touched?.email && errors?.email && (
                        <Text fontSize="sm" color="red.500" mt={1}>
                          {errors?.email}
                        </Text>
                      )}
                    </AuthInputBox>
                  </GridItem>
                  <GridItem colSpan={1} colStart={1} rowStart={3}>
                    <AuthLabelText>{t("signup.phone")}</AuthLabelText>
                    <AuthInputBox>
                      <AuthInputField
                        id="phone"
                        name="phone"
                        type="text"
                        onBlur={handleBlur}
                        placeholder={t(
                          "signup.phonePlaceholder",
                          "01012345678"
                        )}
                        value={values?.phone}
                        onChange={(e) =>
                          setFieldValue("phone", e?.target?.value)
                        }
                      />
                      {touched?.phone && errors?.phone && (
                        <Text fontSize="sm" color="red.500" mt={1}>
                          {errors?.phone}
                        </Text>
                      )}
                    </AuthInputBox>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <AuthLabelText>{t("signup.org")} <Text as="span" color="red.500">*</Text></AuthLabelText>
                    <RadioGroup
                      value={userType}
                      onChange={setUserType}
                      colorScheme="purple"
                      mt={2}
                    >
                      <Stack direction="row" spacing={8}>
                        <Radio
                          value={USER_TYPE.SUBSCRIBER}
                          onChange={(val) => setUserType(USER_TYPE.SUBSCRIBER)}
                        >
                          {t("signup.subscriber")}
                        </Radio>
                        <Radio
                          value={USER_TYPE.NON_SUBSCRIBER}
                          onChange={(val) =>
                            setUserType(USER_TYPE.NON_SUBSCRIBER)
                          }
                        >
                          {t("signup.nonSubscriber")}
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </GridItem>
                </Flex>
                <Flex flexDir={"column"} gap={"22px"} width={["100%", "100%", "50%"]}>
                  {" "}
                  <GridItem colSpan={1}>
                    <AuthLabelText>{t("signup.affOrg")} <Text as="span" color="red.500">*</Text></AuthLabelText>
                    <AuthInputBox>
                      <CustomSelect
                        options={
                          organizationOptions
                            ?.filter((org) => org?.companyName)
                            .map((org) => ({
                              value: org?.id,
                              label: org?.companyName,
                            })) || []
                        }
                        value={values?.organization}
                        onChange={(value) => {
                          setFieldValue("organization", value);
                          fetchDepartments({ subscriberId: value, search: "" });
                        }}
                        placeholder={t(
                          "signup.organizationPlaceholder",
                          "Enter organization name"
                        )}
                        searchPlaceholder={t("signup.organizationSearchPlaceholder", "기관명 검색")}
                        width="100%"
                        borderRadius="12px"
                        mt={0}
                        isSearchable={true}
                        onSearch={(term) => fetchOrganizations(term)}
                      />
                      {touched?.organization && errors?.organization && (
                        <Text fontSize="sm" color="red.500" mt={1}>
                          {errors?.organization}
                        </Text>
                      )}
                    </AuthInputBox>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <AuthLabelText>{t("signup.department")}</AuthLabelText>
                    <AuthInputBox>
                      <CustomSelect
                        options={
                          departmentsData?.rows?.map((dept: any) => ({
                            value: dept?.id,
                            label: dept?.title,
                          })) || []
                        }
                        value={values?.departement}
                        onChange={(value) => {
                          setFieldValue("departement", value);
                        }}
                        placeholder={t("signup.departmentPlaceholder")}
                        searchPlaceholder={t("signup.departmentSearchPlaceholder", "부서 검색")}
                        width="100%"
                        borderRadius="12px"
                        mt={0}
                        isSearchable={true}
                        onSearch={(term) => {
                          if (values?.organization) {
                            fetchDepartments({ subscriberId: values?.organization, search: term });
                          }
                        }}
                        onScrollEnd={() => fetchMoreDepartments()}
                      />
                    </AuthInputBox>
                  </GridItem>

                  <GridItem colSpan={1} colStart={2}>
                    <AuthLabelText>{t("signup.createPassword")} <Text as="span" color="red.500">*</Text></AuthLabelText>
                    <AuthInputBox>
                      <InputGroup>
                        <AuthInputField
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder={t(
                            "signup.passwordPlaceholder",
                            "Enter your password"
                          )}
                          value={values?.password}
                          onChange={(e) =>
                            setFieldValue("password", e?.target?.value)
                          }
                          onBlur={handleBlur}
                        />
                        <InputRightElement height="100%" alignItems="center">
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            icon={showPassword ? <CloseEye /> : <OpenEye />}
                            variant="ghost"
                            _hover={{ background: "transparent" }}
                            size="sm"
                            onClick={() => setShowPassword((v) => !v)}
                            tabIndex={-1}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </AuthInputBox>
                  </GridItem>
                  <GridItem colSpan={1} colStart={2}>
                    <AuthLabelText>{t("signup.confirmPassword")} <Text as="span" color="red.500">*</Text></AuthLabelText>
                    <AuthInputBox>
                      <InputGroup>
                        <AuthInputField
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t(
                            "signup.confirmPasswordPlaceholder",
                            "Confirm the password"
                          )}
                          value={values?.confirmPassword}
                          onChange={(e) =>
                            setFieldValue("confirmPassword", e?.target?.value)
                          }
                          onBlur={handleBlur}
                        />
                        <InputRightElement height="100%" alignItems="center">
                          <IconButton
                            aria-label={
                              showConfirmPassword
                                ? "Hide password"
                                : "Show password"
                            }
                            icon={
                              showConfirmPassword ? <CloseEye /> : <OpenEye />
                            }
                            variant="ghost"
                            _hover={{ background: "transparent" }}
                            size="sm"
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            tabIndex={-1}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </AuthInputBox>

                    {/* Password validation UI */}
                    {touched?.password && (
                      <Box mt={2}>
                        <ValidationCheck
                          isValid={(values?.password?.length || 0) >= 8}
                          text={t("signup.passwordMinLength")}
                        />
                        <ValidationCheck
                          isValid={/\d/.test(values?.password || "")}
                          text={t("signup.passwordMinNumber")}
                        />
                        <ValidationCheck
                          isValid={
                            values?.password &&
                            values?.confirmPassword &&
                            values?.password === values?.confirmPassword
                          }
                          text={t("signup.passwordMatch")}
                        />
                      </Box>
                    )}
                  </GridItem>
                </Flex>
              </Flex>

              {/* Privacy Policy */}
              <Box mt={4}>
                <Flex align="center" gap={2}>
                  <Checkbox
                    id="privacyPolicy"
                    isChecked={values?.privacyPolicy}
                    onChange={(e) => setFieldValue("privacyPolicy", e.target.checked)}
                    colorScheme="purple"
                  />
                  <Text fontSize="sm">
                    <Text
                      as="a"
                      href="https://www.ikpec.or.kr/m2/sub0_privacy_edu.asp"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="#1819B4"
                      textDecoration="underline"
                      marginRight={2}
                    >
                      {t("signup.privacyPolicy", "개인정보 처리방침")}
                    </Text>
                    {t("signup.privacyPolicyAgree", "에 동의합니다")} <Text as="span" color="red.500">*</Text>
                  </Text>
                </Flex>
                {touched?.privacyPolicy && errors?.privacyPolicy && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {errors?.privacyPolicy}
                  </Text>
                )}
              </Box>

              <AuthButtonWrapper>
                <Tooltip
                  label={tooltipMessage}
                  isDisabled={!tooltipMessage}
                  hasArrow
                  placement="top"
                  bg="gray.700"
                  color="white"
                  borderRadius="md"
                  fontSize="sm"
                  px={3}
                  py={2}
                >
                  <Box w={["100%", "100%", "474px"]}>
                    <AuthButton
                      isLoading={loading}
                      isDisabled={!allFieldsFilled}
                      type="submit"
                      fontSize="18px"
                      mt={6}
                      mb={3}
                      minW="100%"
                    >
                      {t("signup.signupLink")}
                    </AuthButton>
                  </Box>
                </Tooltip>
              </AuthButtonWrapper>
            </form>
          );
        }}
      </Formik>
      <Flex justifyContent="center" alignItems="center" width={"100%"}>
        <AuthCheckboxText>{t("signup.alreadyAccount")}</AuthCheckboxText>
        <LinkText to={ROUTE_NAMES.JOURNALIST_LOGIN} color={"#1819B4"} ml={2}>
          {t("login.signIn")}
        </LinkText>
      </Flex>

      {/* Same confirmation modal the rest of the app uses, so this dialog does
          not introduce a second look for the same kind of question. */}
      <ModalDelete
        isOpen={pendingApprovalValues !== null}
        onClose={() => {
          setPendingApprovalValues(null);
          navigate(ROUTE_NAMES.LOGIN);
        }}
        onConfirm={() => {
          const values = pendingApprovalValues;
          setPendingApprovalValues(null);
          if (values) handleSubmit(values, true);
        }}
        title={t("signup.approvalPendingTitle")}
        description={t("signup.approvalPendingMessage")}
        deleteButtonTitle={t("signup.approvalPendingYes")}
        cancelButtonTitle={t("signup.approvalPendingNo")}
      />
    </AuthFormContainer>
  );
};
const ValidationCheck = ({
  isValid,
  text,
}: {
  isValid: boolean | string;
  text: string;
}) => (
  <Flex align="center" mt={1}>
    <Box as="span" color={isValid ? "green.500" : "red.500"} mr={2}>
      {isValid ? "✔" : "✖"}
    </Box>
    <Text fontSize="sm" color={isValid ? "green.500" : "red.500"}>
      {text}
    </Text>
  </Flex>
);
